import { useMutation, useQuery } from '@tanstack/react-query';
import { ShareAPI, ShareResponse } from '..';
import { LocationResponse } from '../types/location-response';
import { ItemResponse } from '../types/item-response';
import { AssetsResponse } from '../types/asset-response';
import { RefundsFilter } from '../../modules/Refunds/types/refunds-response';
import { UserResponse } from '../../modules/Auth/types';
import { PetitionsRequest, PetitionsResponse } from '../../modules/Petitions/types';

interface ThirdParty {
  id: number;
  name: string;
}

interface ListTercerosResponse {
  third_party: ThirdParty[];
}

interface Props {
  user_id?: number;
  responsible_id?: number;
}

export const useEntryManage = (id: number, token: string) => {
  return useQuery<any[], Error>({
    queryKey: ['entryManage'],
    queryFn: () => ShareAPI.entryManage(id, token),
  });
};
export const useListTerceros = (token: string) => {
  return useQuery<ListTercerosResponse, Error>({
    queryKey: ['listTerceros'],
    queryFn: () => ShareAPI.listTerceros(token),
  });
};
export const useEntyTypes = () => {
  return useQuery<ShareResponse[], Error>({
    queryKey: ['entryTypes'],
    queryFn: ShareAPI.entryTypes,
  });
};
export const useItemTypes = () => {
  return useQuery<ShareResponse[], Error>({
    queryKey: ['itemTypes'],
    queryFn: ShareAPI.itemTypes,
  });
};

export const useDeliveryOptions = () => {
  return useQuery<ShareResponse[], Error>({
    queryKey: ['deliveryOptions'],
    queryFn: ShareAPI.deliveryOptions,
  });
};

export const useEntryStates = () => {
  return useQuery<ShareResponse[], Error>({
    queryKey: ['entryStates'],
    queryFn: ShareAPI.entryStates,
  });
};

export const useGoodStates = () => {
  return useQuery<ShareResponse[], Error>({
    queryKey: ['goodStates'],
    queryFn: ShareAPI.goodstates,
  });
};
export const useRefundStates = () => {
  return useQuery<ShareResponse[], Error>({
    queryKey: ['refundStates'],
    queryFn: ShareAPI.refundstates,
  });
};
export const useGoodsConsumos = () => {
  return useQuery<ShareResponse[] | any, Error>({
    queryKey: ['goodsConsumos'],
    queryFn: ShareAPI.goodsConsumos,
  });
};

export const useManageStatus = (type: any) => {
  return useQuery<ShareResponse[], Error>({
    queryKey: ['manageStatus', type],
    queryFn: () => ShareAPI.manageStatus(type),
  });
};

export const useManageStatusGoodItemPetition = () => {
  return useQuery<ShareResponse[], Error>({
    queryKey: ['manageStatusGoodItemPetition'],
    queryFn: ShareAPI.manageStatusGoodItemPetition,
  });
};
export const useAreas = () => {
  return useQuery<ShareResponse[], Error>({
    queryKey: ['areas'],
    queryFn: ShareAPI.areas,
  });
};

export const useSedes = () => {
  return useQuery<ShareResponse[], Error>({
    queryKey: ['sedes'],
    queryFn: ShareAPI.sedes,
  });
};

export const useSupervisors = (filters?: Partial<UserResponse>) => {
  return useQuery<ShareResponse[], Error>({
    queryKey: ['supervisors', filters || {}],
    queryFn: ({ queryKey }) =>ShareAPI.supervisors({ queryKey }),
  });
};

export const useSectionals = (filters?: Partial<UserResponse>) => {
  return useQuery<ShareResponse[], Error>({
    queryKey: ['sectionals', filters || {}],
    queryFn: ({ queryKey }) =>ShareAPI.sectionals({ queryKey }),
  });
};

export const useDependecies = (filters?: Partial<RefundsFilter>) => {
  return useQuery<ShareResponse[], Error>({
    queryKey: ['dependencies', filters || {}],
    queryFn: ({ queryKey }) =>ShareAPI.dependencies({ queryKey })
  });
};
export const useDependeciesByUser = (id: any) => {
  return useQuery<ShareResponse[], Error>({
    queryKey: ['dependeciesByUser', id || ""],
    queryFn: ({ queryKey }) =>ShareAPI.dependenciesIdUser({ queryKey })
  });
};

export const useAssets = (filters?: Partial<AssetsResponse>) => {
  return useQuery<any, Error>({
    queryKey: ['assets', filters || {}],
    queryFn: ({ queryKey }) =>ShareAPI.assets({ queryKey }),
  });
};

export const useAssetsAll = (filters?: Partial<AssetsResponse>) => {
  return useQuery<any, Error>({
    queryKey: ['assets', filters || {}],
    queryFn: ({ queryKey }) =>ShareAPI.assetsAll({ queryKey }),
  });
};

export const useAssetsConsumo = () => {
  return useQuery<any, Error>({
    queryKey: ['assets-consumo'],
    queryFn: () =>ShareAPI.assetsConsumo(),
  });
};

export const useItems = (filters?: Partial<AssetsResponse>) => {
  return useQuery<any, Error>({
    queryKey: ['items', filters || {}],
    queryFn: ({ queryKey }) =>ShareAPI.items({ queryKey }),
  });
};

export const useLocation = () => {
  return useQuery<LocationResponse[], Error>({
    queryKey: ['location'],
    queryFn: ShareAPI.location,
  });
};

export const useItem = () => {
  return useQuery<ItemResponse[], Error>({
    queryKey: ['item'],
    queryFn: ShareAPI.item,
  });
};

export const useAdvanceFilters = (key: string) => {
  return useQuery<ItemResponse[], Error>({
    queryKey: ['advance-filters', key || 'asset'],
    queryFn: () => ShareAPI.advanceFilters(key),
  });
};

export const useGoodsConsumerGoods = () => {
  return useQuery<ItemResponse[], Error>({
    queryKey: ['goods-consumer-goods'],
    queryFn: ShareAPI.goodsConsumerGoods,
  });
};

export const useUpdateGestorItems = () => {
  return useMutation<any[], Error, { body: any; id: number | string, token: string }>({
    mutationFn: async ({ body, id, token }) => ShareAPI.updateGestorItems(body, id, token),
  });
};
export const useUpdateGestorStatus = () => {
  return useMutation<any[], Error, { body: any; token: string, id: number | undefined }>({
    mutationFn: async ({ body, token, id }) => ShareAPI.updateGestorStatus(body, token, id),
  });
};
export const useUpdateGestorEntryStatus = () => {
  return useMutation<any[], Error, { body: any; token: string, id: number | undefined }>({
    mutationFn: async ({ body, token, id }) => ShareAPI.updateGestorEntryStatus(body, token, id),
  });
};
export const useUpdateGestorConceptsStatus = () => {
  return useMutation<any[], Error, { body: any; token: string, id: number | undefined }>({
    mutationFn: async ({ body, token, id }) => ShareAPI.updateGestorConceptsStatus(body, token, id),
  });
};
export const useReturnConcepts = () => {
  return useMutation<any[], Error, { body: any; token: string, id: number | undefined }>({
    mutationFn: async ({ body, token, id }) => ShareAPI.returnConcepts(body, token, id),
  });
};
export const useApproveConcepts = () => {
  return useMutation<any[], Error, { body: any; token: string, id: number | undefined }>({
    mutationFn: async ({ body, token, id }) => ShareAPI.approveConcepts(body, token, id),
  });
};
export const useAddEvalPetitionConcept = () => {
  return useMutation<any[], Error, { body: any; token: string, id: number | undefined }>({
    mutationFn: async ({ body, token, id }) => ShareAPI.addEvalConcept(body, token, id),
  });
};
export const useUpdateEntryStatusDevuelto = () => {
  return useMutation<any[], Error, { body: any; token: string, id: number | undefined }>({
    mutationFn: async ({ body, token, id }) => ShareAPI.updateEntryStatusDevuelto(body, token, id),
  });
};

export const useGetDependeciesByUserLogged = (
  filters?: { user_id?: number; responsible_id?: number, model?: string },
  options?: { enabled?: boolean }
) => {
  const transformedFilters = filters ?? {};
  return useQuery<ShareResponse[], Error>({
    queryKey: ['dependeciesByUserLogged', transformedFilters],
    queryFn: ShareAPI.getDependeciesByUserLogged,
    ...options,
  });
};

export const useGetExportFiles = (id: any) => {
  return useQuery<ShareResponse[], Error>({
    queryKey: ['getExportFiles'],
    queryFn: () => ShareAPI.getExportFiles(id),
  });
};

export const useGetAddittions = () => {
  return useMutation<any[], Error, {assetId: string}>({
    mutationFn: async ({assetId}) => ShareAPI.getAddittions(assetId),
  });
};

export const useGetAssetsList = () => {
  return useMutation<any, Error, Partial<AssetsResponse>>({
    mutationFn: async (filters) => {
      return await ShareAPI.assetsList({ queryKey: ['assetsList', filters || {}] });
    },
  });
};

export const useGetGoodConsumerList = () => {
  return useMutation<any, Error, Partial<AssetsResponse>>({
    mutationFn: async (filters) => {
      return await ShareAPI.GoodConsumerList({ queryKey: ['GoodConsumerList', filters || {}] });
    },
  });
};

export const useManageGoodConsumerItems = () => {
  return useMutation<any[], Error, { body: any, id: number | string }>({
    mutationFn: async ({body, id}) => ShareAPI.manageGoodConsumerItems(body, id),
  });
};

export const useGetUtilsKeys = (keys: string) => {
  return useQuery<any>({
    queryKey: ['UtilsKeys', keys],
    queryFn: () => ShareAPI.fetchParameters(keys),
  });
};